import { useEffect, useMemo, useState } from 'react';
import { ConfigProvider, theme } from 'antd';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProLayout from './layouts/ProLayout';
import EditorLayout from './layouts/EditorLayout';
import SceneEditor from './pages/SceneEditor';
import DashboardEditor from './pages/DashboardEditor';
import DashboardList from './pages/DashboardList';
import DashboardPreview from './pages/DashboardPreview';
import DashboardPublish from './pages/DashboardPublish';
import ScenePreview from './pages/ScenePreview';
import ProjectList from './pages/ProjectList';
import DataSetList from './pages/DataSetList';
import RecycleBin from './pages/RecycleBin';
import RecyclePages from './pages/RecyclePages';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import WechatCallback from './pages/WechatCallback';
import QrLoginPage from './pages/QrLoginPage';
import PopupHost from './components/popup/PopupHost';
import ProjectDeployModal from './components/deploy/ProjectDeployModal';
import QrConfirmPage from './pages/QrConfirmPage';
import Account from './pages/Account';
import Pricing from './pages/Pricing';
import MaterialLibrary from './pages/MaterialLibrary';
import MyAssets from './pages/MyAssets';
import Addons from './pages/Addons';
import LicenseRenewal from './pages/LicenseRenewal';
import RuleEngine from './pages/RuleEngine';
import RuleChains from './pages/RuleChains';
import PluginMarket from './plugins/pages/PluginMarket';
import useAuthStore from './stores/authStore';
import { useBrandStore } from './stores/brandStore';
import LicenseBanner from './components/LicenseBanner';
import { useOemStore } from './stores/oemStore';
import { applyViewerOem } from './viewer/oem';
import SupportWidgetMount from './components/SupportWidgetMount';
import MobileApp from './m/MobileApp';
import { pluginRouteRegistry, defaultPluginRoutes } from './plugins/plugin-routes';
import usePluginStore from './plugins/plugin-manager';

const darkTheme = {
  algorithm: theme.darkAlgorithm,
  token: {
    colorPrimary: '#1890ff',
    colorBgContainer: '#1a1a2e',
    colorBgElevated: '#1a1a2e',
    colorBgLayout: '#0f0f1a',
    colorBorder: '#333',
    colorText: '#e0e0e0',
    colorTextSecondary: '#888',
    borderRadius: 4,
  },
};

pluginRouteRegistry.registerAll(defaultPluginRoutes);

export default function App() {
  const { isAuthenticated } = useAuthStore();
  // E 组：/m 移动端页面不挂客服组件（💬 会盖住底部 Tab 且管理页面无需在线客服）
  const location = useLocation();
  const isMobileRoute = location.pathname === '/m' || location.pathname.startsWith('/m/');
  const [routeVersion, setRouteVersion] = useState(0);
  const syncFromServer = usePluginStore((s) => s.syncFromServer);
  const syncComponentMarket = usePluginStore((s) => s.syncComponentMarket);
  const plugins = usePluginStore((s) => s.plugins);

  // H2：平台品牌（白标 OEM）→ 主题主色跟随品牌配置
  const brand = useBrandStore((s) => s.config);
  const themeConfig = useMemo(
    () => ({
      ...darkTheme,
      token: { ...darkTheme.token, ...(brand?.primaryColor ? { colorPrimary: brand.primaryColor } : {}) },
    }),
    [brand],
  );

  // 保险：首帧渲染后若骨架屏仍在 body（未走 AppLoading 接管的路径），移除避免遮挡
  useEffect(() => {
    const sk = document.getElementById('root-skeleton');
    if (sk) sk.remove();
  }, []);

  // DW-P1-6：主站 OEM 入口（?oemToken=<发布令牌>）→ 品牌标题/图标/登录背景
  const setOem = useOemStore((s) => s.setOem);
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('oemToken');
    if (!token) return;
    fetch(`/api/v1/public/deploy/${encodeURIComponent(token)}/meta`)
      .then((r) => (r.ok ? r.json() : null))
      .then((meta: { oem?: Record<string, unknown> } | null) => {
        if (meta?.oem) {
          setOem(meta.oem as never);
          applyViewerOem(meta.oem);
        }
      })
      .catch(() => {});
  }, [setOem]);

  useEffect(() => {
    return pluginRouteRegistry.subscribe(() => {
      setRouteVersion((v) => v + 1);
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      syncFromServer();
      syncComponentMarket();
    }
  }, [isAuthenticated, syncFromServer, syncComponentMarket]);

  const pluginRoutes = useMemo(() => {
    return pluginRouteRegistry.getRouteObjects();
  }, [routeVersion]);

  if (!isAuthenticated) {
    return (
      <ConfigProvider theme={themeConfig}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/wechat-callback" element={<WechatCallback />} />
          {/* F-06 扫码登录（新端展示二维码，公开） */}
          <Route path="/qr-login" element={<QrLoginPage />} />
          {/* E 组：公众号管理网站 H5（独立登录态，未登录也可访问） */}
          <Route path="/m/*" element={<MobileApp />} />
          {/* 2026-08-26｜修复：未登录访问任意页面，重定向登录时携带完整目标（含 ?preview3d=1 等参数），登录后直达 */}
          <Route
            path="*"
            element={
              <Navigate
                to={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                replace
              />
            }
          />
        </Routes>
        {!isMobileRoute && <SupportWidgetMount />}
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={themeConfig}>
      {/* H2：私有化只读横幅（授权过期且宽限期结束；SaaS 模式不显示） */}
      <LicenseBanner />
      <Routes>
        <Route path="/" element={<Navigate to="/projects" replace />} />
        <Route path="/design/dashboard/:id" element={<EditorLayout><DashboardEditor /></EditorLayout>} />
        <Route path="/preview/dashboard/:id" element={<DashboardPreview />} />
        <Route path="/publish/dashboard/:id" element={<DashboardPublish />} />
        <Route path="/preview/scene/:id" element={<ScenePreview />} />
        <Route path="/editor/scene/:id" element={<EditorLayout><SceneEditor /></EditorLayout>} />
        <Route path="/editor/dashboard/:id" element={<EditorLayout><DashboardEditor /></EditorLayout>} />
        <Route path="/editor/scene/:sceneId/dashboard/:id" element={<EditorLayout><DashboardEditor /></EditorLayout>} />
        <Route path="/wechat-callback" element={<WechatCallback />} />
        {/* F-06 扫码登录（已登录端确认页） */}
        <Route path="/qr-confirm" element={<QrConfirmPage />} />
        {/* E 组：公众号管理网站 H5（独立登录态，与主站登录互不影响） */}
        <Route path="/m/*" element={<MobileApp />} />

        <Route path="*" element={
          <ProLayout>
            <Routes>
              <Route path="/projects" element={<ProjectList />} />
              <Route path="/trash" element={<RecycleBin />} />
              <Route path="/dashboards" element={<DashboardList />} />
              <Route path="/pages/trash" element={<RecyclePages />} />
              <Route path="/datasets" element={<DataSetList />} />
              <Route path="/account" element={<Account />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/plugins" element={<PluginMarket />} />
              <Route path="/materials" element={<MaterialLibrary />} />
              <Route path="/assets" element={<MyAssets />} />
              <Route path="/addons" element={<Addons />} />
              <Route path="/licenses" element={<LicenseRenewal />} />
              <Route path="/rules" element={<RuleEngine />} />
            <Route path="/rule-chains" element={<RuleChains />} />

              {pluginRoutes
                .filter((route) => route.path?.startsWith('/'))
                .map((route, index) => (
                  <Route key={`plugin-inner-${index}`} path={route.path} element={route.element} />
                ))}
            </Routes>
          </ProLayout>
        } />
      </Routes>
      {!isMobileRoute && <SupportWidgetMount />}
      {/* 2026-10-06：弹窗宿主提升到 App 级（列表页导出受限等场景也需弹「套餐订阅/付费推广」；自动弹逻辑仍仅在 3D 编辑器内触发） */}
      <PopupHost />
      {/* DW-P0-8：项目部署对话框（编辑器工具栏/列表卡片统一入口） */}
      <ProjectDeployModal />
    </ConfigProvider>
  );
}
